import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiService } from '../../utils/api';
import { formatDate, parseBackendDate } from '../../utils/datetime';
import { AlbumCard, GalleryGrid } from './GalleryGrid';
import { Calendar, ChevronLeft, ChevronRight, MapPin, Quote, Star, Users } from 'lucide-react';

const dateRange = (event) => (event.eventEndDate && formatDate(event.eventEndDate) !== formatDate(event.eventDate)
  ? `${formatDate(event.eventDate)} – ${formatDate(event.eventEndDate)}`
  : formatDate(event.eventDate));

/** Ongoing or future events count as upcoming; an event without dates is treated as upcoming. */
export const isUpcoming = (event, now = new Date()) => {
  const ends = parseBackendDate(event.eventEndDate || event.eventDate);
  return !ends || ends.getTime() + 24 * 60 * 60 * 1000 > now.getTime();
};

const EventLogo = ({ event }) => (event.logoUrl ? (
  <img src={event.logoUrl} alt="" className="h-12 w-12 shrink-0 rounded-lg object-contain bg-white border" />
) : (
  <div aria-hidden="true" className="h-12 w-12 shrink-0 rounded-lg bg-violet-600 text-white flex items-center justify-center font-semibold">
    {(event.name.match(/\b[A-Za-z]/g) || ['E']).slice(0, 2).join('').toUpperCase()}
  </div>
));

const EventsCarousel = ({ title, subtitle, events, past, link }) => {
  const track = useRef(null);
  const scroll = (direction) => track.current?.scrollBy({ left: direction * track.current.clientWidth * 0.8, behavior: 'smooth' });
  return (
    <section className={`py-14 ${past ? 'bg-gray-50' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-lg text-gray-600">{subtitle}</p>}
            {link}
          </div>
          {events.length > 3 && (
            <div className="hidden sm:flex gap-2">
              <button type="button" aria-label="Previous" onClick={() => scroll(-1)} className="rounded-full border bg-white p-2 hover:bg-gray-50"><ChevronLeft className="h-5 w-5" /></button>
              <button type="button" aria-label="Next" onClick={() => scroll(1)} className="rounded-full border bg-white p-2 hover:bg-gray-50"><ChevronRight className="h-5 w-5" /></button>
            </div>
          )}
        </div>
        <div ref={track} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {events.map((event) => (
            <div key={event.id} className={`snap-start shrink-0 w-[80%] sm:w-[45%] lg:w-[31%] rounded-xl border border-gray-200 p-5 flex flex-col ${past ? 'bg-white' : 'bg-gradient-to-br from-violet-50 to-indigo-50'}`}>
              <div className="flex items-center gap-3 mb-3">
                <EventLogo event={event} />
                <h3 className="font-semibold text-gray-900 line-clamp-2">{event.name}</h3>
              </div>
              <div className="space-y-1 text-sm text-gray-600 flex-1">
                {event.eventDate && <div className="flex items-center"><Calendar className="h-4 w-4 mr-2 shrink-0" />{dateRange(event)}</div>}
                {event.location && <div className="flex items-center"><MapPin className="h-4 w-4 mr-2 shrink-0" /><span className="truncate">{event.location}</span></div>}
              </div>
              <div className="mt-4 flex items-center justify-between gap-2">
                <span className="flex items-center text-violet-700 font-semibold">
                  <Users className="h-4 w-4 mr-1" />{event.registrations.toLocaleString('en-IN')} {past ? 'attendees' : 'registered'}
                </span>
                {past
                  ? <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">Completed</span>
                  : event.registrationOpen
                    ? <Link to={`/register/${event.slug || event.id}`} className="text-sm font-medium text-violet-600 hover:underline">Register →</Link>
                    : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Registration closed</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/** One gallery section: event albums when there are any, otherwise the latest photos. */
const GallerySection = ({ albums, items }) => (
  <section className="py-14 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Gallery</h2>
          <p className="text-lg text-gray-600">Moments from events we have powered</p>
        </div>
        <Link to="/gallery" className="text-violet-600 hover:underline font-medium">View all photos →</Link>
      </div>
      {albums.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {albums.map((album) => <AlbumCard key={album.id} album={album} />)}
        </div>
      ) : (
        <GalleryGrid items={items.slice(0, 12)} />
      )}
    </div>
  </section>
);

const LogoStrip = ({ logos }) => (
  <section className="py-10 bg-white border-y">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="mb-6 text-center text-sm font-medium uppercase tracking-wide text-gray-500">Trusted by organisers and institutions</p>
      <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
        {logos.map((logo) => {
          const img = <img src={ApiService.galleryPhotoUrl(logo.id)} alt={logo.caption || ''} title={logo.caption || ''} loading="lazy"
            className="h-12 w-auto max-w-[160px] object-contain grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition" />;
          return logo.link
            ? <a key={logo.id} href={logo.link} target="_blank" rel="noopener noreferrer">{img}</a>
            : <span key={logo.id}>{img}</span>;
        })}
      </div>
    </div>
  </section>
);

const useWebsiteContent = () => useQuery({
  queryKey: ['websiteContent'],
  queryFn: () => ApiService.getWebsiteContent(),
  staleTime: 60 * 1000,
  retry: false,
}).data;

const Testimonials = ({ items }) => (
  <section className="py-16 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What organisers say</h2>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => (
          <figure key={t.id} className="rounded-xl border border-gray-200 p-6 bg-gradient-to-br from-white to-violet-50">
            <Quote className="h-6 w-6 text-violet-300 mb-2" />
            {t.rating && (
              <div className="flex mb-2" aria-label={`${t.rating} out of 5`}>
                {Array.from({ length: 5 }, (_, i) => (
                  <Star key={i} className={`h-4 w-4 ${i < t.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                ))}
              </div>
            )}
            <blockquote className="text-gray-700 whitespace-pre-line">{t.message}</blockquote>
            <figcaption className="mt-4">
              <div className="font-semibold text-gray-900">{t.name}</div>
              {t.title && <div className="text-sm text-gray-500">{t.title}</div>}
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-8 text-center text-sm text-gray-600">
        Ran an event with us? <Link to="/testimonial" className="text-violet-600 hover:underline">Share your experience</Link>
      </p>
    </div>
  </section>
);

/** Upcoming events, gallery, past events and partner logos: what we show clients first. */
const HomeShowcase = ({ showLogos = true }) => {
  const data = useWebsiteContent();
  if (!data) return null;
  const events = data.events || [];
  const upcoming = events.filter((event) => isUpcoming(event))
    .sort((a, b) => (parseBackendDate(a.eventDate)?.getTime() || 0) - (parseBackendDate(b.eventDate)?.getTime() || 0));
  const past = events.filter((event) => !isUpcoming(event));
  return (
    <>
      {upcoming.length > 0 && (
        <EventsCarousel title="Upcoming events" subtitle="Register for events running on EventEase" events={upcoming}
          link={<Link to="/events" className="mt-1 inline-block text-sm font-medium text-violet-600 hover:underline">See all upcoming events →</Link>} />
      )}
      {(data.albums?.length > 0 || data.gallery?.length > 0) && <GallerySection albums={data.albums || []} items={data.gallery || []} />}
      {past.length > 0 && <EventsCarousel title="Past events" subtitle="Organisers who ran their registrations with us" events={past} past />}
      {showLogos && data.logos?.length > 0 && <LogoStrip logos={data.logos} />}
    </>
  );
};

export const HomeLogos = () => {
  const data = useWebsiteContent();
  return data?.logos?.length > 0 ? <LogoStrip logos={data.logos} /> : null;
};

/** Totals across the events listed on the website, for the stats band. */
export const useWebsiteTotals = () => {
  const data = useWebsiteContent();
  const events = data?.events || [];
  return {
    events: events.length,
    registrations: events.reduce((sum, event) => sum + (Number(event.registrations) || 0), 0),
  };
};

export const HomeTestimonials = () => {
  const data = useWebsiteContent();
  return data?.testimonials?.length > 0 ? <Testimonials items={data.testimonials} /> : null;
};

export default HomeShowcase;
